import { Component, computed, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { KENDO_CARD, KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { AbstractManageComponent } from '@unity/abstract/abstract-manage.component';
import { AgreementsClient, AgreementUserDetailedDto, AgreementUsersClient, TransitionRequestDto, UserUpdateDto } from '@unity/clients';
import { AuthorizeDirective } from '@unity/directives/authorize.directive';
import { TextboxReadonlyControlComponent } from '@unity/forms/textbox-readonly-control/textbox-readonly-control.component';
import { FormLayoutComponent } from '@unity/layouts/form-layout/form-layout.component';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { ActionDeleteSharedComponent } from '@unity/shared/composed/action-delete-shared/action-delete-shared.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { PanelManageSharedComponent } from '@unity/shared/composed/panel-manage-shared/panel-manage-shared.component';
import { stringFormControl } from '@unity/utils/form-controls';
import { QuickActions } from '@unity/utils/quick-actions';
import { FormGroupControlSchema, UnityEntitlementPath } from '@unity/utils/types';
import { EMPTY, of } from 'rxjs';

// This is here purely to satisfy the leaky abstraction of AbstractManageComponent, which requires an UpdateDTO + Schema.
// Since AgreementUser has no Update functionality, this does not actually do anything.
class FakeSchema implements FormGroupControlSchema<UserUpdateDto> {
  public full_name: FormControl<string>;

  public constructor(user?: AgreementUserDetailedDto) {
    this.full_name = stringFormControl({ disabled: true, value: user?.full_name });
  }
}

@Component({
  selector: 'unity-manage-agreement-user-page',
  imports: [
    KENDO_CARD,
    KENDO_STACKLAYOUT,
    PageContainerComponent,
    ActionDeleteSharedComponent,
    AuthorizeDirective,
    FormLayoutComponent,
    TextboxReadonlyControlComponent,
    PanelManageSharedComponent,
    ActionSharedComponent,
  ],
  templateUrl: './manage-agreement-user-page.component.html',
  styleUrl: './manage-agreement-user-page.component.scss',
})
export class ManageAgreementUserPageComponent extends AbstractManageComponent<AgreementUserDetailedDto, UserUpdateDto, FakeSchema> {
  private readonly agreementsClient = inject(AgreementsClient);
  private readonly agreementUsersClient = inject(AgreementUsersClient);

  /* Resources */
  protected override mainResource = rxResource({
    params: () => {
      const agreementId = this.$agreementId();
      const userId = this.$userId();
      return userId && agreementId ? { agreementId, userId } : undefined;
    },
    stream: ({ params: { agreementId, userId } }) => this.agreementUsersClient.getOneForAgreementAsDetailed(agreementId, userId),
  });

  protected agreementResource = rxResource({
    params: () => {
      const agreementId = this.$agreementId();
      return agreementId ? { agreementId } : undefined;
    },
    stream: ({ params: { agreementId } }) => this.agreementsClient.getOneAsMini(agreementId),
  });
  /* Inputs */
  public readonly $agreementId = input.required({ alias: 'agreementId', transform: (value: string) => parseInt(value) });
  public readonly $userId = input.required({ alias: 'userId', transform: (value: string) => parseInt(value) });

  /* Signals */
  protected readonly $agreement = computed(() => (this.agreementResource.hasValue() ? this.agreementResource.value() : undefined));
  protected readonly $contentHeader = computed(() => `Agreement User: ${this.$resource()?.full_name}`);
  protected readonly $subHeader = computed(() => `Agreement: ${this.$agreement()?.name}`);

  /* Impl */
  protected override availableTransitionsFn$ = (_userId: number) => of([]);
  protected override updateResourceFn$ = (_userId: number, _form: FormGroup<FakeSchema>) => EMPTY;
  protected override deleteResourceFn$ = (userId: number) => this.agreementUsersClient.deleteOneFromAgreement(this.$agreementId(), userId);
  protected override doTransitionFn$ = (_resourceId: number, _transition: TransitionRequestDto) => EMPTY;

  protected override newControlsFn = (_dto?: AgreementUserDetailedDto | undefined) => new FakeSchema();

  protected override resourceName = 'Agreement User';
  protected override editEntitlement: UnityEntitlementPath = 'Agreement:Update';
  protected override deleteEntitlement: UnityEntitlementPath = 'Agreement:Update';
  protected override transitionEntitlement: UnityEntitlementPath = 'Agreement:Update';

  protected override $resourceId = this.$userId;

  protected readonly $returnToAgreementUserListAction = computed(() => QuickActions.ReturnToAgreementUsersList(this.$agreementId()));
}
